// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A table whose columns can be widened or narrowed by dragging the edge of a header
 * (design doc §10.5).
 *
 * **How.** The handles are an overlay, positioned over the header's column edges, not
 * nodes added inside the `<th>`s — React owns those cells and would fight anything
 * inserted into them. The first drag freezes every column at its current width and
 * switches the table to fixed layout, so widening one column does not reflow the others;
 * from then on the dragged column and the table width change together, and the table
 * scrolls sideways in its wrapper if it outgrows the page.
 *
 * **Widths are a floor, not a cap.** Once the drag ends, the table goes back to filling
 * its container, with the chosen widths as its minimum: given more room — a wider window,
 * a wider dialog — every column grows in proportion, rather than the table staying at the
 * width it was dragged to with the rest of the row left empty and the serials clipped.
 *
 * **Remembered** per table (`storageKey`) in this browser, because a width someone set
 * for a 400-line BOM is one they want back tomorrow. Remembered widths apply only while
 * the table has the same number of columns — a draft's extra actions column, or a column
 * added in a later release, would otherwise shift every width by one. Double-click a
 * handle to go back to automatic widths. Storage that throws (private mode) just means
 * nothing is remembered.
 */

import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

const MIN_WIDTH = 40;
const STORAGE_PREFIX = "cybertron.columns.";

interface Props {
  /** Names this table's remembered widths, e.g. "bom-detail". */
  storageKey: string;
  className?: string;
  /** On the scrolling wrapper, e.g. to cap its height so the header can stay in view. */
  wrapClassName?: string;
  children: ReactNode;
}

interface Edge {
  left: number;
  top: number;
  height: number;
}

export function ResizableTable({ storageKey, className, wrapClassName, children }: Props) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [edges, setEdges] = useState<Edge[]>([]);

  const headers = useCallback((): HTMLTableCellElement[] => {
    const head = tableRef.current?.tHead;
    const row = head?.rows[head.rows.length - 1];
    return row ? Array.from(row.cells) : [];
  }, []);

  const measure = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    const height = table.tHead?.offsetHeight ?? 0;
    // A handle straddles its edge (3px either side), so the last one is pulled in to end
    // at the table's right edge rather than poke past it and scroll the wrapper sideways.
    const right = table.offsetLeft + table.offsetWidth - 4;
    setEdges(
      headers().map((cell) => ({
        left: Math.min(table.offsetLeft + cell.offsetLeft + cell.offsetWidth, right),
        top: table.offsetTop,
        height,
      })),
    );
  }, [headers]);

  const apply = useCallback(
    (widths: number[], { stretch }: { stretch: boolean }) => {
      const table = tableRef.current;
      if (!table) return;
      headers().forEach((cell, index) => {
        cell.style.width = `${widths[index]}px`;
      });
      const total = `${widths.reduce((sum, width) => sum + width, 0)}px`;
      table.style.tableLayout = "fixed";
      // While dragging, exactly the widths under the pointer; at rest, at least them.
      table.style.width = stretch ? "100%" : total;
      table.style.minWidth = stretch ? total : "";
    },
    [headers],
  );

  // Back to automatic widths: the table's own layout, nothing pinned.
  const clear = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    for (const cell of headers()) cell.style.width = "";
    table.style.tableLayout = "";
    table.style.width = "";
    table.style.minWidth = "";
  }, [headers]);

  // Restore what was remembered whenever the table becomes a different one — another
  // key, or a column shown or hidden under the same key (a search adding its found-at
  // column). The widths pinned for the old set come off first: left on the header cells
  // that React kept, they would size the new columns by the old ones' widths, and hold
  // the table at the old total. Checked on every render, since a column can appear
  // without the key changing.
  const appliedFor = useRef<string | null>(null);
  useLayoutEffect(() => {
    const count = headers().length;
    const layout = `${storageKey}:${count}`;
    if (appliedFor.current === layout) return;
    if (appliedFor.current !== null) clear();
    appliedFor.current = layout;
    const saved = readWidths(storageKey);
    if (saved && saved.length === count) apply(saved, { stretch: true });
    measure();
  });

  // Re-place the handles whenever the table changes size (rows loading, a column's
  // content, the window).
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(table);
    return () => observer.disconnect();
  }, [measure]);

  const startDrag = (index: number) => (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const cells = headers();
    const startX = event.clientX;
    const startWidths = cells.map((cell) => cell.offsetWidth || MIN_WIDTH);
    apply(startWidths, { stretch: false });

    const onMove = (move: PointerEvent) => {
      const widths = [...startWidths];
      widths[index] = Math.max(
        MIN_WIDTH,
        (startWidths[index] ?? MIN_WIDTH) + move.clientX - startX,
      );
      apply(widths, { stretch: false });
      measure();
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const widths = headers().map(
        (cell) => Number.parseFloat(cell.style.width) || cell.offsetWidth,
      );
      writeWidths(storageKey, widths);
      apply(widths, { stretch: true });
      measure();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const reset = () => {
    clear();
    writeWidths(storageKey, null);
    measure();
  };

  return (
    <div
      className={wrapClassName ? `resizable-table ${wrapClassName}` : "resizable-table"}
      ref={wrapRef}
    >
      <table ref={tableRef} className={className}>
        {children}
      </table>
      {edges.map((edge, index) => (
        <span
          // One per column edge, in column order; the set changes only with the columns.
          // biome-ignore lint/suspicious/noArrayIndexKey: edges have no identity but position
          key={index}
          className="col-resizer tooltip-trigger"
          role="separator"
          aria-orientation="vertical"
          aria-label={t("table.resizeColumn", { n: index + 1 })}
          data-tooltip={t("table.resizeHint")}
          style={{ left: edge.left - 3, top: edge.top, height: edge.height }}
          onPointerDown={startDrag(index)}
          onDoubleClick={reset}
        />
      ))}
    </div>
  );
}

function readWidths(storageKey: string): number[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((width) => typeof width === "number")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function writeWidths(storageKey: string, widths: number[] | null): void {
  try {
    if (widths) window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(widths));
    else window.localStorage.removeItem(STORAGE_PREFIX + storageKey);
  } catch {
    // Nothing remembered — the widths still apply for this visit.
  }
}
